import type { GlobalSearchHit } from './types';

/**
 * Route resolution for View Details on a search hit.
 *
 * Each document type resolves to a new-module route (SPA navigation); a hit a
 * resolver can't handle (missing id/field) falls back to the backend-supplied
 * `wizard_url`, which targets the OLD admin portal — its routes (/get-quotes,
 * /wizard, /non-motor-orders/...) don't exist in this app. Mapping table:
 * docs/rewrite/global-search.md.
 */
export type ResolvedRoute =
  | { kind: 'internal'; path: string }
  | { kind: 'external'; url: string }
  // Opened via openOpsDashboardWithAuth so the embed-vs-redirect switch and
  // auth-token handling stay in one place (same call the All Policies
  // "See Quotation" action makes).
  | { kind: 'ops-dashboard'; params: Record<string, string> }
  | null;

/** `"quotation-1234"` → `1234`. Null when no trailing number exists. */
export const numericIdFrom = (compositeId: string): number | null => {
  const match = /(\d+)$/.exec(compositeId);
  return match ? Number(match[1]) : null;
};

/**
 * Pull the affiliate id out of the backend-baked `wizard_url`. The
 * quotation_query ES document doesn't index the affiliate id as its own field —
 * only the wizard_url carries it (`...&affiliate=<id>&...`), so it's the one
 * place the FE can recover it from. Null on absent/non-numeric values
 * (the BE bakes `affiliate=None` when the FK is unset).
 */
export const affiliateIdFromWizardUrl = (wizardUrl?: string): number | null => {
  if (!wizardUrl) return null;
  try {
    const value = new URL(wizardUrl).searchParams.get('affiliate');
    return value && /^\d+$/.test(value) ? Number(value) : null;
  } catch {
    return null;
  }
};

type RouteResolver = (hit: GlobalSearchHit) => ResolvedRoute;

const NEW_ROUTE_RESOLVERS: Partial<Record<string, RouteResolver>> = {
  // Plan listing (new-portal successor of the legacy /get-quotes destination).
  // The page consumes `quotation_query_id` + `affiliate` from the URL; the
  // vehicle category is derived from the FQQ itself, so the legacy
  // `vmi_vehicle_category` param is not forwarded. Without an affiliate the
  // price-list query can't be scoped, so fall back to the VI Leads deep-link
  // (usePipelineDeepLink) rather than land on a broken quote session.
  quotation_query: (hit) => {
    if (hit.quotation_query_id == null) return null;
    const affiliateId = affiliateIdFromWizardUrl(hit.wizard_url);
    if (affiliateId == null) {
      return { kind: 'internal', path: `/voluntary-insurance/leads?quotation_query_id=${hit.quotation_query_id}` };
    }
    return {
      kind: 'internal',
      path: `/plan-listing?quotation_query_id=${hit.quotation_query_id}&affiliate=${affiliateId}`,
    };
  },
  // OPS Dashboard — same destination as the All Policies "See Quotation"
  // action, keyed by quotation_query_id (the ES doc indexes it as a string).
  motor_insurance: (hit) => {
    if (hit.quotation_query_id == null) return null;
    return { kind: 'ops-dashboard', params: { quotation_query_id: String(hit.quotation_query_id) } };
  },
  // CMI list deep-link: ?cif_id= filters the list via the cif_ids API param.
  compulsory_insurance: (hit) => {
    const id = numericIdFrom(hit.id);
    return id != null ? { kind: 'internal', path: `/compulsory-insurance?cif_id=${id}` } : null;
  },
  // Non-motor detail page keys on the Qoala source_order_id, same as the hit field.
  qoala_sub_order: (hit) => {
    if (hit.source_order_id == null) return null;
    return { kind: 'internal', path: `/non-motor-details/${hit.source_order_id}` };
  },
  // Agent detail (same path the backend wizard_url uses, but SPA-internal).
  affiliate: (hit) => {
    const id = numericIdFrom(hit.id);
    return id != null ? { kind: 'internal', path: `/crm/affiliate/${id}` } : null;
  },
};

export function resolveHitRoute(hit: GlobalSearchHit): ResolvedRoute {
  const resolved = NEW_ROUTE_RESOLVERS[hit.document_type]?.(hit) ?? null;
  if (resolved) return resolved;
  return hit.wizard_url ? { kind: 'external', url: hit.wizard_url } : null;
}
